import { useEffect, useState, useCallback } from 'react';
import { Button, Badge } from '@/backoffice/components/ui';
import {
  fetchPermissionCatalog,
  listUserPermissions,
  syncUserPermissions,
  permissionLabel,
} from '@/domains/api/permissions.api';
import type { ApiNamedResource } from '@/domains/api/types';
import { Save, Shield } from 'lucide-react';

interface UserPermissionsEditorProps {
  userId: number;
  /** super_admin peut modifier ; admin en lecture seule sur super_admin */
  readOnly?: boolean;
  onSaved?: () => void;
}

export function UserPermissionsEditor({ userId, readOnly = false, onSaved }: UserPermissionsEditorProps) {
  const [catalog, setCatalog] = useState<ApiNamedResource[]>([]);
  const [assigned, setAssigned] = useState<ApiNamedResource[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allPerms, userPerms] = await Promise.all([
        fetchPermissionCatalog(),
        listUserPermissions(userId),
      ]);
      const merged = new Map<number, ApiNamedResource>();
      [...allPerms, ...userPerms].forEach((p) => merged.set(p.id, p));
      const mergedList = [...merged.values()].sort((a, b) => a.name.localeCompare(b.name));
      setCatalog(mergedList);
      setAssigned(userPerms);
      setSelected(userPerms.map((p) => p.id));
    } catch {
      setError('Impossible de charger les permissions.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: number) => {
    if (readOnly) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await syncUserPermissions(userId, selected);
      setAssigned(updated);
      setSelected(updated.map((p) => p.id));
      onSaved?.();
    } catch {
      setError('Échec de la synchronisation des permissions.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 py-2">Chargement des permissions...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-gray-500" />
        <p className="text-sm font-medium text-gray-700">Permissions</p>
        {assigned.length > 0 && (
          <Badge variant="secondary" size="sm">{assigned.length} active(s)</Badge>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {catalog.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune permission disponible dans le catalogue.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {catalog.map((perm) => (
            <label
              key={perm.id}
              className={`flex items-center gap-2 p-2.5 rounded-lg border transition-colors ${
                readOnly ? 'bg-gray-50 cursor-default' : 'bg-white cursor-pointer hover:bg-violet-50 hover:border-violet-200'
              } ${selected.includes(perm.id) ? 'border-violet-300 bg-violet-50' : 'border-gray-200'}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(perm.id)}
                disabled={readOnly}
                onChange={() => toggle(perm.id)}
                className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-sm text-gray-800">{permissionLabel(perm.name)}</span>
              <span className="text-xs text-gray-400 ml-auto">{perm.name}</span>
            </label>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            leftIcon={<Save className="w-4 h-4" />}
            isLoading={saving}
            onClick={handleSave}
          >
            Enregistrer les permissions
          </Button>
        </div>
      )}
    </div>
  );
}
